import React, {useCallback, useEffect, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {Platform} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {SettingsStackParamList} from '@/app/routes';
import {
  clearPushRegistration,
  pushAvailable,
  pushStatus,
  syncPushRegistration,
  PUSH_PROVIDER_NAME,
  type PushStatus,
} from '@/shared/push';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {Page} from '@/shared/ui/page';
import {SettingsNav} from '@/pages/settings/SettingsNav';
import {Section} from '@/shared/ui/section';
import {StatusPill, type StatusTone} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {spacing} from '@/shared/ui/theme';

/**
 * «Ещё» → Уведомления на этом устройстве.
 *
 * Not to be confused with «Уведомления» under Организация, which is a COMPANY switch
 * deciding whether arrivals and departures are announced at all, to Telegram. This one
 * is about THIS phone: whether the platform has an address to deliver to.
 *
 * The screen exists mostly to make an invisible thing visible. A push that never
 * arrives has no failure to report anywhere — no error, no log line, nothing on any
 * screen — so a user's only evidence is silence, which is indistinguishable from
 * "nothing happened". Naming the state, including the honest `unconfigured`, is the
 * difference between a feature that is off and a feature that is broken.
 */
type Props = NativeStackScreenProps<SettingsStackParamList, 'PushSettings'>;

const styles = StyleSheet.create({
  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  grow: {flex: 1},
});

function toneFor(status: PushStatus): StatusTone {
  switch (status) {
    case 'registered':
      return 'online';
    case 'denied':
      return 'offline';
    default:
      return 'neutral';
  }
}

export function PushSettingsScreen(_: Props) {
  const {t} = useTranslation();
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    void pushStatus().then(setStatus);
  }, []);

  useEffect(refresh, [refresh]);

  const available = pushAvailable();

  return (
    <Page
      sideNav={<SettingsNav />}
      scroll
      width="form"
      testID="push-settings"
      title={t('push.title')}
      >

      <Section title={t('push.state')} hint={t('push.stateHint')}>
        <Card padding={4}>
          <View style={styles.row}>
            <AppText variant="subtitle" style={styles.grow}>
              {t(`push.status.${status ?? 'idle'}`)}
            </AppText>
            <StatusPill
              tone={toneFor(status ?? 'idle')}
              size="sm"
              label={t(`push.badge.${status ?? 'idle'}`)}
            />
          </View>
        </Card>
      </Section>

      <Section>
        {!available ? (
          // Said plainly rather than hidden behind a disabled switch: the reason is
          // not this device, and it is not the user's to fix. Both halves are waiting
          // on the same thing — the server side of arrival pushes is off until
          // Firebase exists too — so promising a switch here would be promising
          // something no button can deliver.
          <Banner
            tone="info"
            title={t('push.unconfiguredTitle')}
            message={t('push.unconfiguredHint', {provider: PUSH_PROVIDER_NAME})}
          />
        ) : (
          <>
            <AppButton
              title={t(status === 'registered' ? 'push.disable' : 'push.enable')}
              tone={status === 'registered' ? 'neutral' : 'signal'}
              icon="bell"
              loading={busy}
              onPress={() => {
                setBusy(true);
                const work =
                  status === 'registered'
                    ? clearPushRegistration().then<PushStatus>(() => 'idle')
                    : syncPushRegistration(`SamVision · ${Platform.OS}`);
                void work
                  .then(setStatus)
                  .finally(() => setBusy(false));
              }}
              testID="push-toggle"
            />
            {status === 'denied' ? (
              <Banner tone="warning" title={t('push.deniedTitle')} message={t('push.deniedHint')} />
            ) : null}
          </>
        )}
      </Section>

      <Section>
        <Banner tone="info" title={t('push.linkTitle')} message={t('push.linkHint')} />
      </Section>
    </Page>
  );
}
